import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import {
  $createNodeSelection,
  $getNearestNodeFromDOMNode,
  $getSelection,
  $isNodeSelection,
  $isRootNode,
  $setSelection,
  $addUpdateTag,
  $getNodeByKey,
  $getRoot,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  defineExtension,
  HISTORY_PUSH_TAG,
  isDOMNode,
  isHTMLElement,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
  safeCast,
  type LexicalEditor,
  type LexicalExtension,
  type NodeKey,
} from "lexical";
import { CONTENT_CLASSES } from "../../core/content-classes";
import { DEFAULT_LIMITS } from "../../core/limits";
import { BlockIdExtension, registerBlockIdentity } from "../block-id/extension";
import { $rootBlockOf } from "../block-id/operations";
import { $getBlockId } from "../block-id/state";
import { trusted } from "../clipboard/trusted";
import {
  ADD_ATTACHMENTS_COMMAND,
  PICK_ATTACHMENTS_COMMAND,
  SET_ATTACHMENT_ALT_COMMAND,
} from "./commands";
import { createIntake, type Intake, type IntakeConfig } from "./intake";
import { $isAttachmentNode, AttachmentNode, renderers, type AttachmentRender } from "./node";

export interface AttachmentsConfig extends IntakeConfig {
  /**
   * Draws an attachment, or `null` where nothing is drawn (a server seeding a shared document).
   * Read once, when the editor is built: a node is only asked to draw itself when it is created or
   * changed, and changing every node to hand it a renderer later would be an edit.
   */
  render: AttachmentRender | null;
}

/** Files on their way in: how many the host's store has not answered for, and the last refusal. */
export type AttachmentsOutput = Pick<Intake, "subscribe" | "getState" | "dismiss"> & {
  /**
   * Writes an image's description, as one undo step. Here so that what draws an attachment can
   * reach the command through the extension it is handed, without importing anything itself.
   */
  describe(nodeKey: NodeKey, alt: string): void;
  /** For `register` only. */
  internal: { intake: Intake };
};

/** The class a selected attachment's element carries, beside whatever marks a selected block. */
export const ATTACHMENT_SELECTED_CLASS = "ze-attachment-selected";

/** Controls inside an attachment keep their clicks; anywhere else on it selects the block. */
const INTERACTIVE = 'a, button, input, textarea, select, [contenteditable="true"]';

/**
 * What the document format says of a stored attachment, kept true whatever gets past the ways of
 * making one: it holds a reference, and it stands at the top level. One without a reference (a
 * peer's half-made node) is removed, since there is nothing of it to keep. One that lands inside
 * another block moves out to directly after that block; a decorator is a leaf, and leaves are
 * transformed before elements, so this runs before a table cell would turn it into nothing.
 */
/**
 * Whether a paste is a picture and nothing else. A browser that copies an image puts the file on
 * the clipboard and, beside it, markup that is one `<img>` pointing at where the image came from;
 * upstream sees the markup, takes the paste for text, and the image is lost. An office
 * application also sends a file beside its markup (a picture of the cells that were copied), and
 * there the markup is the content. So the markup decides: one image and no words is a picture.
 */
function isPictureOnly(html: string): boolean {
  const body = new DOMParser().parseFromString(trusted(html), "text/html").body;
  return body.querySelectorAll("img").length === 1 && (body.textContent ?? "").trim() === "";
}

/** A file dialog, opened from inside the event that asked for it and gone once it has answered. */
function pickFiles(editor: LexicalEditor, accept: string, take: (files: File[]) => void): void {
  const owner = editor.getRootElement()?.ownerDocument;
  if (owner === undefined) return;
  const input = owner.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.accept = accept;
  input.addEventListener("change", () => take([...(input.files ?? [])]), { once: true });
  input.click();
}

/** As much as `checkAttachmentRef` takes. */
const MAX_ALT_LENGTH = 2000;

function $keepAttachment(node: AttachmentNode): void {
  if (node.getAttachment() === null) return node.remove();
  const parent = node.getParent();
  if (parent === null || $isRootNode(parent)) return;
  $rootBlockOf(node)?.insertAfter(node);
}

/**
 * Attachments: the node, its place in the document, and selecting it. Loaded on demand, like
 * tables: see `src/react/attachments.ts`. No React here, because `@zsherman/editor/collab/seed` builds the
 * same editor on a server.
 */
export const AttachmentsExtension: LexicalExtension<
  AttachmentsConfig,
  "z-editor/attachments",
  AttachmentsOutput,
  unknown
> = defineExtension({
  name: "z-editor/attachments",
  config: safeCast<AttachmentsConfig>({
    render: null,
    documentId: "",
    adapter: { current: null },
    limits: DEFAULT_LIMITS,
    accept: [],
    reject: { current: () => {} },
    $place: () => null,
    tray: null,
  }),
  dependencies: [BlockIdExtension],
  nodes: () => [AttachmentNode],
  build(editor, config): AttachmentsOutput {
    const intake = createIntake(editor, config);
    return {
      subscribe: intake.subscribe,
      getState: intake.getState,
      dismiss: intake.dismiss,
      describe: (nodeKey, alt) =>
        void editor.dispatchCommand(SET_ATTACHMENT_ALT_COMMAND, {
          target: { nodeKey },
          alt,
          outcome: { refused: null },
        }),
      internal: { intake },
    };
  },
  register(editor, config, state) {
    const { intake } = state.getOutput().internal;
    if (config.render !== null) renderers.set(editor, config.render);
    let marked: ReadonlySet<NodeKey> = new Set();
    return mergeRegister(
      // Files that were dropped, or pasted with nothing else on the clipboard. Upstream has put
      // the caret where they were dropped by now, and has already consumed the event either
      // way: unhandled, a dropped file simply vanishes.
      editor.registerCommand(
        DRAG_DROP_PASTE,
        (files) => {
          const outcome = intake.$add(files);
          return outcome.refused !== "not-offered";
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const data = "clipboardData" in event ? event.clipboardData : null;
          if (data === null || data.files.length === 0) return false;
          const html = data.getData("text/html");
          // No markup: upstream hands the files over itself. Markup with words in it: a paste of
          // content, which is not this handler's.
          if (html === "" || !isPictureOnly(html)) return false;
          if (intake.$add([...data.files]).refused === "not-offered") return false;
          event.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerCommand(
        ADD_ATTACHMENTS_COMMAND,
        (payload) => {
          Object.assign(payload.outcome, intake.$add(payload.files));
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        PICK_ATTACHMENTS_COMMAND,
        (payload) => {
          if (config.adapter.current === null) payload.outcome.refused = "not-offered";
          else if (!editor.isEditable()) payload.outcome.refused = "read-only";
          else {
            const images = config.accept.filter((type) => type.startsWith("image/"));
            const accept =
              payload.kind === "image" ? (images.length > 0 ? images : ["image/*"]) : config.accept;
            // The dialog answers long after this update is over, and the caret has not moved
            // meanwhile: a file dialog is modal.
            pickFiles(editor, accept.join(","), (files) => {
              editor.read(() => intake.$add(files));
            });
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      registerBlockIdentity(editor, AttachmentNode),
      editor.registerNodeTransform(AttachmentNode, $keepAttachment),
      editor.registerCommand(
        SET_ATTACHMENT_ALT_COMMAND,
        ({ target, alt, outcome }) => {
          const node =
            "nodeKey" in target
              ? $getNodeByKey(target.nodeKey)
              : ($getRoot()
                  .getChildren()
                  .find((child) => $getBlockId(child) === target.blockId) ?? null);
          const attachment = $isAttachmentNode(node) ? node.getAttachment() : null;
          if (node === null) outcome.refused = "unknown-block";
          else if (!$isAttachmentNode(node) || attachment === null || alt.length > MAX_ALT_LENGTH) {
            outcome.refused = "invalid-target";
          } else if (attachment.alt !== alt) {
            node.setAttachment({ ...attachment, alt });
            $addUpdateTag(HISTORY_PUSH_TAG);
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      // A control inside an attachment (Retry, Open) is inside the editable surface, so its keys
      // reach the editor before they reach it: Enter on a focused button would break the line the
      // caret is on and never press the button. Such a key is the control's and nobody else's.
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          const target = event.target;
          if (!isHTMLElement(target) || target === editor.getRootElement()) return false;
          const control = target.closest(INTERACTIVE);
          return (
            control !== null &&
            control !== editor.getRootElement() &&
            control.closest(`.${CONTENT_CLASSES.attachment}`) !== null
          );
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const target = event.target;
          if (!isDOMNode(target)) return false;
          const node = $getNearestNodeFromDOMNode(target);
          if (!$isAttachmentNode(node)) return false;
          // A control inside the attachment keeps its click. Inside it, and not around it: the
          // editor's own root is `contenteditable` too.
          const control = isHTMLElement(target) ? target.closest(INTERACTIVE) : null;
          if (control !== null && editor.getElementByKey(node.getKey())?.contains(control)) {
            return false;
          }
          // A selection of one root block, which is what block selection is: its keys, its
          // toolbar, and copying all work on it from here.
          const selection = $createNodeSelection();
          selection.add(node.getKey());
          $setSelection(selection);
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      // The element is this node's own (`createDOM`), and selection is not part of the node, so
      // it is marked from outside, as upstream marks a selected rule.
      editor.registerUpdateListener(({ editorState }) => {
        const selected = editorState.read(() => {
          const selection = $getSelection();
          return new Set(
            $isNodeSelection(selection)
              ? selection
                  .getNodes()
                  .filter($isAttachmentNode)
                  .map((node) => node.getKey())
              : [],
          );
        });
        for (const key of new Set([...marked, ...selected])) {
          editor
            .getElementByKey(key)
            ?.classList.toggle(ATTACHMENT_SELECTED_CLASS, selected.has(key));
        }
        marked = selected;
      }),
      () => renderers.delete(editor),
      () => intake.dispose(),
    );
  },
});
